import { useCallback, useEffect, useRef } from 'react'
import { useFocusEffect } from 'expo-router'
import { useApiClient } from '../../../helpers/apiClient.helper'
import { API_ENDPOINTS } from '../../../constants/api'

const THROTTLE_MS = 35_000

export interface UseConsumerBillingStripeSyncOptions {
  enabled: boolean
  /** When set with `activeNav`, runs sync when user switches to that tab (e.g. Plan & billing). */
  activeNav?: string
  planBillingNavMatch?: string
}

/**
 * After Stripe Customer Portal (or when webhooks lag), pull consumer Pro subscription from Stripe
 * into our DB, then refresh account admin state. Throttled to avoid hammering Stripe.
 *
 * - Runs on screen focus (e.g. `/packs`, `/account-admin`).
 * - Optional: when `activeNav === planBillingNavMatch`, runs on that tab (same screen, different nav).
 */
export function useConsumerBillingStripeSync(
  refresh: () => Promise<void>,
  options: UseConsumerBillingStripeSyncOptions,
) {
  const apiClient = useApiClient()
  const lastSyncAt = useRef(0)
  /** Avoid parallel runs when useFocusEffect and tab useEffect fire together. */
  const syncInFlightRef = useRef(false)

  const syncThenRefresh = useCallback(async () => {
    if (syncInFlightRef.current) {
      return
    }
    syncInFlightRef.current = true
    try {
      const now = Date.now()
      const elapsed = now - lastSyncAt.current
      if (elapsed < THROTTLE_MS) {
        await refresh()
        return
      }
      lastSyncAt.current = now
      try {
        await apiClient<{ success?: boolean }>(API_ENDPOINTS.billing.consumerProSyncSubscription, {
          method: 'POST',
        })
      } catch {
        // Best-effort; webhooks may still update. Always refresh from DB.
      }
      await refresh()
    } finally {
      syncInFlightRef.current = false
    }
  }, [apiClient, refresh])

  useFocusEffect(
    useCallback(() => {
      if (!options.enabled) {
        return undefined
      }
      let cancelled = false
      void (async () => {
        if (cancelled) return
        await syncThenRefresh()
      })()
      return () => {
        cancelled = true
      }
    }, [options.enabled, syncThenRefresh]),
  )

  useEffect(() => {
    if (!options.enabled) return
    if (options.planBillingNavMatch == null || options.activeNav == null) return
    if (options.activeNav !== options.planBillingNavMatch) return
    void syncThenRefresh()
  }, [options.enabled, options.activeNav, options.planBillingNavMatch, syncThenRefresh])
}
